import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import {
    fetchPaymentMethods,
    createPaymentMethod,
    updatePaymentMethod,
    updateUserProfile
} from '../services/api';
import isoCountryCodes from '../iso_country_codes.json';
import { usePermissions } from '../Hooks/usePermission';
import { PERMISSIONS } from '../lib/utils/permissions';
import { USER_ROLES } from '../lib/utils/constant';
import { InfoCircleFill } from 'react-bootstrap-icons';
import {
    sanitizeLocationFieldInput,
    validateLocationField,
} from '../lib/utils/validation';

// --- Icons ---
const PersonIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /></svg>;
const CardIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" /></svg>;
const PencilIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.378.378-.106.547-.547-.547.547z" /></svg>;

const validCountryCodes = new Set(isoCountryCodes.map(({ code }) => code.toUpperCase()));

const normalizeCountryToCode = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const upperRaw = raw.toUpperCase();
    if (/^[A-Za-z]{2}$/.test(raw) && validCountryCodes.has(upperRaw)) return upperRaw;
    const mapped = isoCountryCodes.find(code => code.countryName.toLowerCase() === raw.toLowerCase());
    return mapped?.code || "";
};

const mapBillingFieldToLocationField = (name) => {
    if (name === 'billingZip') return 'zipCode';
    if (name === 'billingCity') return 'city';
    if (name === 'billingState') return 'state';
    if (name === 'billingCountry') return 'country';
    return null;
};

// --- Helper: Debounce Hook ---
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
}

function ProfileModal({ show, onHide, user, onProfileUpdate, canCreatePayment }) {
    const {can} = usePermissions();
    const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
    const canUpdateUser = can(PERMISSIONS.USER_UPDATE,userRoleCode);
    const [view, setView] = useState('main'); // 'main' | 'paymentForm'

    // --- Profile State ---
    const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', email: '' });
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [profileErrors, setProfileErrors] = useState({}); // New Validation State

    // --- Payment State ---
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isLoadingPayments, setIsLoadingPayments] = useState(false);
    const [isSavingPayment, setIsSavingPayment] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null);
    const [isFetchingBillingZip, setIsFetchingBillingZip] = useState(false);

    const [paymentForm, setPaymentForm] = useState({
        cardName: '', nickname: '', cardNumber: '', cardExpiry: '', cardCvc: '',
        billingAddress: '', billingCity: '', billingState: '', billingZip: '', billingCountry: '',
        isDefault: false
    });
    const [paymentErrors, setPaymentErrors] = useState({});

    // --- Generic Modal State ---
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const debouncedBillingZip = useDebounce(paymentForm.billingZip, 500);
    const roleCode = (user?.role?.code || user?.roleCode || user?.role?.name || '').toUpperCase();
    const isSysAdmin = roleCode === USER_ROLES.SYS_ADMIN
    const isSysUser = roleCode === USER_ROLES.SYS_USER ;
    const isClientAccount = user?.entityMaster?.accountType === USER_ROLES.CLIENT;
    const isEntityAccount = user?.entityMaster?.accountType === USER_ROLES.ENTITY;

    // Allow Payment Management only for CLIENT_ADMIN or ENTITY_ADMIN (or ADMIN with account type)
    // Explicitly excludes SYS_ADMIN and USER
    const isChargeToCustomer = user?.entityMaster?.chargeToCustomer;

    // --- Logic: Reset State on Modal Open ---
    useEffect(() => {
        if (show) {
            // Clear errors
            setProfileErrors({});
            setPaymentErrors({});
            // Reset view to main
            setView('main');

            // Initialize form data from user prop
            if (user) {
                setProfileForm({
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    email: user.emailAddress || user.email || '',
                });
            }

            if (show && view === 'main' && isClientAccount) {
                loadPaymentMethods();
            }
        }
    }, [ show, user, isClientAccount]);

    // --- Logic: Billing Zipcode Autofill ---
    const fetchZipcodeData = useCallback(async (zip) => {
        if (!zip || zip.length < 5) return;
        setIsFetchingBillingZip(true);
        try {
            const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
            if (!response.ok) throw new Error('Zipcode not found');
            const data = await response.json();
            const place = data.places[0];

            setPaymentForm(prev => ({
                ...prev,
                billingCity: place['place name'],
                billingState: place['state abbreviation'],
                billingCountry: data['country abbreviation']
            }));
        } catch (error) {
            console.error("Zipcode fetch error:", error);
        } finally {
            setIsFetchingBillingZip(false);
        }
    }, []);

    useEffect(() => {
        if (debouncedBillingZip) fetchZipcodeData(debouncedBillingZip);
    }, [debouncedBillingZip, fetchZipcodeData]);

    const loadPaymentMethods = async () => {
        setIsLoadingPayments(true);
        try {
            const response = await fetchPaymentMethods();
            if (response.data && response.data.success) {
                // Map API response to UI model
                const methods = (response.data.data.items || []).map(item => ({
                    id: item.id,
                    cardName: item.name || 'Card',
                    nickname: item.nickname || '',
                    cardType: item.cardType,
                    last4: item.last4,
                    expiry: `${String(item.expiryMonth).padStart(2, '0')}/${String(item.expiryYear).slice(-2)}`,
                    isDefault: item.isDefault,
                    billing: {
                        address: item.addressLine1,
                        city: item.city,
                        state: item.state,
                        zip: item.postalCode,
                        country: item.country
                    }
                }));
                setPaymentMethods(methods);
            }
        } catch (error) {
            console.error("Error loading payment methods", error);
        } finally {
            setIsLoadingPayments(false);
        }
    };

    // --- Profile Handlers ---
    const handleProfileChange = (e) => {
        setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
        // Clear error on change
        if (profileErrors[e.target.name]) {
            setProfileErrors(prev => ({ ...prev, [e.target.name]: '' }));
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        // Validation
        const errors = {};
        if (!profileForm.firstName.trim()) errors.firstName = 'First Name is required';
        if (!profileForm.lastName.trim()) errors.lastName = 'Last Name is required';

        if (Object.keys(errors).length > 0) {
            setProfileErrors(errors);
            return;
        }


        setIsSavingProfile(true);
        try {
            if (user && user.id) {
                const payload = {
                    firstName: profileForm.firstName,
                    lastName: profileForm.lastName,
                };

                await updateUserProfile(payload);
                // --- NEW: Update parent state immediately ---
                if (onProfileUpdate) {
                    onProfileUpdate({ ...user, ...payload });
                }
                setSuccessMsg("Profile updated successfully");
                setShowSuccessModal(true);
            }
        } catch (err) {
            console.error("Failed to update profile:", err);
            setErrorMsg(err.response?.data?.message || "Failed to update profile.");
            setShowErrorModal(true);
        } finally {
            setIsSavingProfile(false);
        }
    };

    // --- Payment View Handlers ---
    const openPaymentForm = (method = null) => {
        setPaymentErrors({});
        setSuccessMsg('');
        setErrorMsg('');
        if (method) {
            setEditingPaymentId(method.id);
            setPaymentForm({
                cardName: method.cardName || '',
                nickname: method.nickname || '',
                cardNumber: method.last4 ? `**** **** **** ${method.last4}` : '',
                cardExpiry: method.expiry || '',
                cardCvc: '***',
                billingAddress: method.billing?.address || '',
                billingCity: method.billing?.city || '',
                billingState: method.billing?.state || '',
                billingZip: method.billing?.zip || '',
                billingCountry: method.billing?.country || '',
                isDefault: method.isDefault || false
            });
        } else {
            setEditingPaymentId(null);
            setPaymentForm({
                cardName: '', nickname: '', cardNumber: '', cardExpiry: '', cardCvc: '',
                billingAddress: '', billingCity: '', billingState: '', billingZip: '', billingCountry: '',
                isDefault: paymentMethods.length === 0
            });
        }
        setView('paymentForm');
    };

    // --- Real-time Validation Helper ---
    const validateField = (name, value) => {
        let error = "";
        const cleanValue = value ? value.replace(/\s/g, '') : '';

        switch (name) {
            case 'cardNumber':
                if (value && !/^\d{13,19}$/.test(value.replace(/\s/g, '')) && !value.includes('*')) {
                    error = "Invalid card number.";
                } else if (value && !value.includes('*') && /^0+$/.test(cleanValue)) {
                    error = "Card number cannot be all zeros.";
                }
                break;
            case 'cardCvc':
                if (value && !/^\d{3,4}$/.test(value) && !value.includes('*')) {
                    error = "Invalid CVC (3-4 digits).";
                } else if (/^0+$/.test(value) && !value.includes('*')) {
                    error = "CVC cannot be all zeros.";
                }
                break;
            case 'cardExpiry':
                if (value && !value.includes('*')) {
                    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) {
                        error = "Format must be MM/YY";
                    } else {
                        const [month, year] = value.split('/');
                        const currentYear = new Date().getFullYear() % 100;
                        const currentMonth = new Date().getMonth() + 1;
                        const expMonth = parseInt(month, 10);
                        const expYear = parseInt(year, 10);

                        if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
                            error = "Card has expired";
                        }
                    }
                }
                break;
            //  case 'billingZip':
            //      if (value && !/^\d{5}$/.test(value)) error = "Must be 5 digits.";
            // break;
            case 'billingZip': {
                const zipError = validateLocationField('zipCode', value);
                if (zipError) error = zipError;
                break;
            }
            case 'billingCity':
                error = validateLocationField('city', value) || "";
                break;
            case 'billingState':
                error = validateLocationField('state', value) || "";
                break;
            case 'billingCountry':
                error = validateLocationField('country', value) || "";
                break;
            default: break;
        }
        return error;
    };

    const handlePaymentChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        const name = e.target.name;
        let newValue = value;

        // --- Input Restrictions (Digits Only) ---
        if ((name === 'cardNumber' || name === 'cardCvc') && !newValue.includes('*')) {
            // Only filter if not masked
            newValue = value.replace(/\D/g, '');
        }

        // --- Auto-format Card Number (XXXX XXXX XXXX XXXX) ---
        if (name === 'cardNumber' && !newValue.includes('*')) {
            const digitsOnly = newValue.slice(0, 19);
            newValue = digitsOnly.replace(/(.{4})/g, '$1 ').trim();
        }

        // --- Auto-format Expiry (MM/YY) ---
        if (name === 'cardExpiry' && !newValue.includes('*')) {
            const digits = value.replace(/\D/g, '');
            // Limit to 4 digits (MMYY) then format
            const truncated = digits.slice(0, 4);
            if (truncated.length >= 3) {
                newValue = `${truncated.slice(0, 2)}/${truncated.slice(2)}`;
            } else {
                newValue = truncated;
            }
        }

        const locationFieldName = mapBillingFieldToLocationField(name);
        if (locationFieldName) {
            newValue = sanitizeLocationFieldInput(locationFieldName, value);
        }

        setPaymentForm({ ...paymentForm, [name]: newValue });

        if (e.target.type !== 'checkbox') {
            const errorMsg = validateField(name, newValue);
            setPaymentErrors(prev => ({ ...prev, [name]: errorMsg }));
        }
    };

    const validatePaymentForm = () => {
        const errors = {};
        let isValid = true;

        if (!paymentForm.cardName) { errors.cardName = 'Required'; isValid = false; }

        const numErr = validateField('cardNumber', paymentForm.cardNumber);
        if (numErr) { errors.cardNumber = numErr; isValid = false; }
        else if (!paymentForm.cardNumber) { errors.cardNumber = 'Required'; isValid = false; }

        const cvcErr = validateField('cardCvc', paymentForm.cardCvc);
        if (cvcErr) { errors.cardCvc = cvcErr; isValid = false; }
        else if (!paymentForm.cardCvc) { errors.cardCvc = 'Required'; isValid = false; }

        const expErr = validateField('cardExpiry', paymentForm.cardExpiry);
        if (expErr) { errors.cardExpiry = expErr; isValid = false; }
        else if (!paymentForm.cardExpiry) { errors.cardExpiry = 'Required'; isValid = false; }

        if (!paymentForm.billingAddress) { errors.billingAddress = 'Required'; isValid = false; }

        const zipErr = validateField('billingZip', paymentForm.billingZip);
        if (zipErr) { errors.billingZip = zipErr; isValid = false; }
        else if (!paymentForm.billingZip) { errors.billingZip = 'Required'; isValid = false; }

        const billingCityErr = validateField('billingCity', paymentForm.billingCity);
        if (billingCityErr) { errors.billingCity = billingCityErr; isValid = false; }

        const billingStateErr = validateField('billingState', paymentForm.billingState);
        if (billingStateErr) { errors.billingState = billingStateErr; isValid = false; }

        const billingCountryErr = validateField('billingCountry', paymentForm.billingCountry);
        if (billingCountryErr) { errors.billingCountry = billingCountryErr; isValid = false; }

        setPaymentErrors(errors);
        return isValid;
    };

    const handleSavePayment = async () => {
        if (!validatePaymentForm()) return;
        setIsSavingPayment(true);

        const normalizedCountryCode = normalizeCountryToCode(paymentForm.billingCountry);
        if (!normalizedCountryCode) {
            setPaymentErrors(prev => ({
                ...prev,
                billingCountry: "Enter a valid country code (e.g., US) or supported country name."
            }));
            setIsSavingPayment(false);
            return;
        }

        const [expMonth, expYear] = paymentForm.cardExpiry.split('/');
        const payload = {
            type: "CARD",
            name: paymentForm.cardName,
            nickname: paymentForm.nickname?.trim() || null,
            isDefault: paymentForm.isDefault,
            card: {
                cardNumber: paymentForm.cardNumber,
                cvc: paymentForm.cardCvc,
                expiryMonth: parseInt(expMonth),
                expiryYear: parseInt("20" + expYear)
            },
            billingAddress: {
                addressLine1: paymentForm.billingAddress,
                city: paymentForm.billingCity,
                state: paymentForm.billingState,
                postalCode: paymentForm.billingZip,
                country: normalizedCountryCode
            }
        };

        try {
            if (editingPaymentId) {
                await updatePaymentMethod(editingPaymentId, payload);
                setSuccessMsg("Payment method updated successfully.");
            } else {
                await createPaymentMethod(payload);
                setSuccessMsg("Payment method added successfully.");
            }
            setShowSuccessModal(true);
            await loadPaymentMethods();
        } catch (err) {
            console.error("Failed to save payment method:", err);
            setErrorMsg(err.response?.data?.message || "Failed to save payment method.");
            setShowErrorModal(true);
        } finally {
            setIsSavingPayment(false);
        }
    };

    // Specific handler for OK button in success modal
    const handleModalOk = () => {
        setShowSuccessModal(false);
        setView('main'); // Return to main view after success acknowledgment
    };

    return (
      <>
        <style type="text/css">
          {`
          .modal-backdrop-dark {
            opacity: 0.8 !important;
          }
        `}
        </style>
        <Modal
          show={show}
          onHide={onHide}
          size="xl"
          centered
          scrollable
          backdrop="static"
          backdropClassName="modal-backdrop-dark"
        >
          <Modal.Header closeButton={view === "main"}>
            <div className="d-flex align-items-center">
              {/* {view === 'paymentForm' && (
                        <Button variant="link" className="p-0 me-3 text-secondary" onClick={() => setView('main')}>
                            <ChevronLeftIcon /> Back
                        </Button>
                    )} */}
              <Modal.Title className="h5 fw-bold text-primary">
                {view === "main"
                  ? "My Profile"
                  : editingPaymentId
                    ? "Edit Payment Method"
                    : "Add Payment Method"}
              </Modal.Title>
            </div>
          </Modal.Header>

          <Modal.Body className="bg-light p-4">
            {/* === VIEW 1: MAIN PROFILE === */}
            {view === "main" && (
              <>
                <Card className="mb-4 shadow-sm border-0">
                  <Card.Header className="bg-white p-3 border-bottom">
                    <h6 className="mb-0 text-secondary fw-bold d-flex align-items-center">
                      <span className="me-2 text-primary">
                        <PersonIcon />
                      </span>{" "}
                      Personal Information
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <Form onSubmit={handleSaveProfile}>
                      <Row className="mb-3">
                        <Form.Group as={Col} md={6} controlId="firstName">
                          <Form.Label className="small text-muted fw-bold">
                            First Name*
                          </Form.Label>
                          <Form.Control
                            name="firstName"
                            value={profileForm.firstName}
                            onChange={handleProfileChange}
                            isInvalid={!!profileErrors.firstName}
                          />
                          <Form.Control.Feedback type="invalid">
                            {profileErrors.firstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md={6} controlId="lastName">
                          <Form.Label className="small text-muted fw-bold">
                            Last Name*
                          </Form.Label>
                          <Form.Control
                            name="lastName"
                            value={profileForm.lastName}
                            onChange={handleProfileChange}
                            isInvalid={!!profileErrors.lastName}
                          />
                          <Form.Control.Feedback type="invalid">
                            {profileErrors.lastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>
                      <Row className="mb-3">
                        <Form.Group as={Col} md={12} controlId="email">
                          <Form.Label className="small text-muted fw-bold">
                            Email
                          </Form.Label>
                          <Form.Control
                            name="email"
                            value={profileForm.email}
                            disabled
                            className="bg-light"
                          />
                        </Form.Group>
                      </Row>
                      {canUpdateUser && (
                        <div className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            size="sm"
                            type="submit"
                            disabled={isSavingProfile}
                          >
                            {isSavingProfile ? "Saving..." : "Update Profile"}
                          </Button>
                        </div>
                      )}
                    </Form>
                  </Card.Body>
                </Card>

{(!isSysAdmin && !isSysUser) && (
                  <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white p-3 border-bottom d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 text-secondary fw-bold d-flex align-items-center">
                        <span className="me-2 text-primary">
                          <CardIcon />
                        </span>{" "}
                        Payment Methods
                        <InfoCircleFill
                          style={{ cursor: "pointer" }}
                          className="ms-2 text-primary"
                          title="The payment method on file will be charged for all devices used within your organization."
                        />
                      </h6>
                      {isClientAccount &&
                        !isChargeToCustomer &&
                        canCreatePayment && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openPaymentForm()}
                          >
                            <PlusIcon /> Add New
                          </Button>
                        )}
                    </Card.Header>
                    {isClientAccount &&
                    !isChargeToCustomer &&
                    canCreatePayment ? (
                      <Card.Body className="p-3">
                        {isLoadingPayments ? (
                          <div className="text-center py-3">
                            <Spinner
                              animation="border"
                              size="sm"
                              variant="primary"
                            />
                          </div>
                        ) : paymentMethods.length === 0 ? (
                          <div className="text-center text-muted py-3">
                            No payment methods added.
                          </div>
                        ) : (
                          <div className="d-flex flex-column gap-3">
                            {paymentMethods.map((method) => (
                              <div
                                key={method.id}
                                className="border rounded p-3 bg-white d-flex justify-content-between align-items-start gap-3"
                              >
                                <div className="d-flex gap-3 flex-grow-1 min-w-0">
                                  <div className="mt-1 text-secondary">
                                    {/* You could render different icons based on method.cardType here */}
                                    <CardIcon />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="fw-bold d-flex align-items-center gap-2 flex-wrap">
                                      {/* Display Card Type if available */}
                                      {method.cardType
                                        ? method.cardType.toUpperCase()
                                        : "Card"}{" "}
                                      •••• {method.last4}
                                      {method.isDefault && (
                                        <Badge
                                          bg="primary"
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          Default
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="text-muted small mt-1">
                                      {method.cardName}
                                    </div>
                                    <div className="text-muted small">
                                      Expires {method.expiry}
                                    </div>
                                    <div className="small text-muted mt-1">
                                      <strong>Billing Address:</strong>{" "}
                                      {method.billing.address},{" "}
                                      {method.billing.city},{" "}
                                      {method.billing.state},{" "}
                                      {method.billing.country}{" "}
                                      {method.billing.zip}
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                                  {method.nickname ? (
                                    <span
                                      className="badge fw-semibold"
                                      style={{
                                        fontSize: "0.72rem",
                                        backgroundColor: "#cff4fc",
                                        color: "#055160",
                                        border: "1px solid #9eeaf9",
                                      }}
                                    >
                                      {method.nickname}
                                    </span>
                                  ) : null}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-secondary"
                                    onClick={() => openPaymentForm(method)}
                                  >
                                    <PencilIcon />
                                  </Button>
                                  {/* {!method.isDefault && (
                                                        <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => handleDeletePayment(method.id)}>
                                                            <TrashIcon />
                                                        </Button>
                                                    )} */}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card.Body>
                    ) : isEntityAccount ? (
                      <p className="text-center mt-4 text-danger">
                        Payment management is available only for users
                        associated with client organizations.
                      </p>
                    ) : (
                      <p className="text-center mt-4 text-danger">
                        Your role doesn’t have sufficient permissions to manage
                        payments for your organization.
                      </p>
                    )}
                  </Card>
                )}
              </>
            )}

            {/* === VIEW 2: PAYMENT FORM === */}
            {view === "paymentForm" && (
              <Form>
                <h6 className="mb-3 text-primary">Card Details</h6>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="default-card-switch"
                    label="Set as default payment method"
                    name="isDefault"
                    checked={paymentForm.isDefault}
                    onChange={handlePaymentChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Card Name</Form.Label>
                  <Form.Control
                    name="nickname"
                    value={paymentForm.nickname}
                    onChange={handlePaymentChange}
                    placeholder="Enter a name for this card"
                  />
                  <Form.Text className="text-muted">
                    e.g., Sales Card, Personal Card
                  </Form.Text>
                </Form.Group>

                <Row className="mb-3">
                  <Form.Group as={Col} md={6}>
                    <Form.Label>Name on Card *</Form.Label>
                    <Form.Control
                      name="cardName"
                      value={paymentForm.cardName}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.cardName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.cardName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6}>
                    <Form.Label>Card Number *</Form.Label>
                    <Form.Control
                      name="cardNumber"
                      value={paymentForm.cardNumber}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.cardNumber}
                      placeholder="XXXX XXXX XXXX XXXX"
                      inputMode="numeric"
                      disabled={!!editingPaymentId} // Disable if editing
                      className={!!editingPaymentId ? "bg-light" : ""}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.cardNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row className="mb-4">
                  <Form.Group as={Col} md={6}>
                    <Form.Label>Expiry (MM/YY) *</Form.Label>
                    <Form.Control
                      name="cardExpiry"
                      value={paymentForm.cardExpiry}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.cardExpiry}
                      placeholder="MM/YY"
                      inputMode="numeric"
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.cardExpiry}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6}>
                    <Form.Label>CVC *</Form.Label>
                    <Form.Control
                      name="cardCvc"
                      value={paymentForm.cardCvc}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.cardCvc}
                      placeholder="cvc"
                      inputMode="numeric"
                      maxLength={4}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.cardCvc}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                <h6 className="mb-3 text-primary">Billing Address</h6>
                <Row className="mb-3">
                  <Form.Group as={Col} md={12}>
                    <Form.Label>Street Address *</Form.Label>
                    <Form.Control
                      name="billingAddress"
                      value={paymentForm.billingAddress}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.billingAddress}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.billingAddress}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row className="mb-3">
                  <Form.Group as={Col} md={6}>
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      name="billingCity"
                      value={paymentForm.billingCity}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.billingCity}
                      maxLength={50}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.billingCity}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6}>
                    <Form.Label>State *</Form.Label>
                    <Form.Control
                      name="billingState"
                      value={paymentForm.billingState}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.billingState}
                      maxLength={50}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.billingState}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group as={Col} md={6}>
                    <Form.Label>Postal Code *</Form.Label>
                    <Form.Control
                      name="billingZip"
                      value={paymentForm.billingZip}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.billingZip}
                      maxLength={20}
                    />
                    {isFetchingBillingZip && (
                      <Spinner animation="border" size="sm" className="ms-2" />
                    )}
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.billingZip}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6}>
                    <Form.Label>Country *</Form.Label>
                    <Form.Control
                      name="billingCountry"
                      value={paymentForm.billingCountry}
                      onChange={handlePaymentChange}
                      isInvalid={!!paymentErrors.billingCountry}
                      maxLength={50}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.billingCountry}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                <hr className="my-4" />
                <div className="d-flex justify-content-end gap-2">
                  <Button variant="light" onClick={() => setView("main")}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSavePayment}
                    disabled={isSavingPayment}
                  >
                    {isSavingPayment ? "Saving..." : "Save Card"}
                  </Button>
                </div>
              </Form>
            )}
          </Modal.Body>

          {/* Footer only for Main View */}
          {view === "main" && (
            <Modal.Footer className="bg-light border-0">
              <Button variant="secondary" onClick={onHide}>
                Close
              </Button>
            </Modal.Footer>
          )}

          {/* --- Nested Success Modal --- */}
          <Modal
            show={showSuccessModal}
            onHide={handleModalOk}
            centered
            backdrop="static"
            className="modal-on-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title className="text-success">Success</Modal.Title>
            </Modal.Header>
            <Modal.Body>{successMsg}</Modal.Body>
            <Modal.Footer>
              <Button variant="success" onClick={handleModalOk}>
                OK
              </Button>
            </Modal.Footer>
          </Modal>

          {/* --- Nested Error Modal --- */}
          <Modal
            show={showErrorModal}
            onHide={() => setShowErrorModal(false)}
            centered
            className="modal-on-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title className="text-danger">Error</Modal.Title>
            </Modal.Header>
            <Modal.Body>{errorMsg || "Something went wrong."}</Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowErrorModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Modal>
      </>
    );
}

export default ProfileModal;